import { useAuth } from '@context-providers/auth-context-provider'
import { Brand, useTheme } from '@context-providers/theme-context-provider'
import { AuthorizedPageLayout } from '@pages/authorized-page-layout'
import { AuthErrorPage } from '@pages/error/auth-error.page'
import { createFileRoute } from '@tanstack/react-router'
import { Program } from '@types'

import { useEffect, useState } from 'react'

import { useUserPrograms } from '@hooks/use-user-programs'
import { useUserStorageProgramId } from '@hooks/use-user-storage-program-id'
import { InfoCard } from '@postxl/ui-components'

/**
 * Auth Route Guard
 * all routes that require authentication are rendered with auth context
 * if the user is not authenticated, when accessing an auth-route, the redirect to the login page is executed
 *
 * after the user is successfully authenticated - the route layout is rendered this way:
 *  #1 load all user programs
 *  #2 check if local storage program id exists in user-programs
 *  #2.1 yes => get program | no => get first program of user-programs
 *  #3 get theme-key from selected program and set theme as selected
 *  #4 continue rendering layout
 */
export const Route = createFileRoute('/_auth-routes')({
  component: AuthGuard,
})

function AuthGuard() {
  const navigate = Route.useNavigate()
  const { setBrand } = useTheme()
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null)

  const [programIdStorage, setProgramIdStorage] = useUserStorageProgramId()
  const { programs: userPrograms, isLoaded, isError: isProgramError } = useUserPrograms()
  const { loginState } = useAuth()

  useEffect(() => {
    if (loginState === 'loggedOut') {
      // store the redirect url as search param, if not authorized, to restore after keycloak login
      void navigate({ to: '/login', search: { redirectUrl: location.pathname + location.search } })
    }
  }, [loginState, navigate])

  // check if local storage program id exists in user-programs
  useEffect(() => {
    // update theme by selected program
    if (isLoaded && userPrograms.length > 0 && !selectedProgram) {
      const firstUserProgram = userPrograms.find((p) => p.id === userPrograms[0].id)

      // check if local storage program id exists in user-programs
      const programFromLocalStorage = userPrograms.find((p) => p.id === programIdStorage)

      // store new program id
      const program = programFromLocalStorage ?? firstUserProgram!
      setSelectedProgram(program)
      setProgramIdStorage(program.id)

      // update theme
      setBrand(program.themeKey as Brand)
    }
  }, [isLoaded, programIdStorage, selectedProgram, setBrand, setProgramIdStorage, userPrograms])

  // Don't render anything until authenticated (redirect effect handles navigation)
  if (loginState !== 'loggedIn') {
    return null
  }

  // if an error occurs while fetching the user programs, show an error message
  if (isProgramError || (isLoaded && userPrograms.length === 0)) {
    // use component instead of 'navigate' to /auth-error, because isLoaded is set to `true` - two render cycles before
    // `userPrograms` contains data
    return <AuthErrorPage errorType={'errorNoProgram'} />
  }

  // still loading data
  if (!selectedProgram) {
    return <InfoCard variant="loading" title="Loading Programs" message="Please wait while we load your programs..." />
  }

  return <AuthorizedPageLayout />
}
